import React, { useContext, useState } from 'react';
import ServerContext from '../../../../contexts/serverContext';
import { Breadcrumbs as LegacyBreadcrumbs } from '@own/fds-react';
import { Breadcrumbs } from '../../breadcrumbs/breadcrumbs';
import { StringUtil } from '../../../utils/string-util/string-util';
import { DyfFeatureGuideBreadcrumbAemContent } from '../../../../views/dyf-landing-view/hooks/use-dyf-feature-guide-breadcrumbs-content';
import './dyf-feature-guide-breadcrumbs.scss';
import { useDyfQueryParams } from '../../../../hooks/dyf/use-dyf-query-params/use-dyf-query-params';
import { FdsChevron } from '../../../common/chevron/fds-chevron';

interface Props {
    origin: 'landing' | 'overview' | 'related' | 'videos';
    dyfFeatureGuideBreadcrumbs: DyfFeatureGuideBreadcrumbAemContent;
    vinDetails: { vin: string; vehicleName: string } | null;
    ymmDetails: { modelName: string } | null;
}

export const DyfFeatureGuideBreadCrumbs = (props: Props) => {
    const [breadcrumbToggle, setBreadCrumbToggle] = useState(false);
    const {
        queryYear = '',
        queryModel = '',
        queryVin = '',
        queryCategory = '',
    } = useDyfQueryParams();

    const { brand, root } = useContext(ServerContext);

    const getBaseRoot = (root: string | undefined): string => {
        return root ? root.substring(0, root.length - 1) : '';
    };

    const baseRoot = getBaseRoot(root);

    const breadcrumbsItems = generateBreadcrumbsItems(
        props,
        baseRoot,
        queryModel,
        queryYear,
        queryVin,
        queryCategory,
        brand
    );

    const newBreadcrumbsItems = [...breadcrumbsItems];
    newBreadcrumbsItems.shift();
    const currentPageBreadcrumbTitle = newBreadcrumbsItems.pop()?.name;

    const brandBreadcrumbDelimiter = brand === 'lincoln' ? `>` : `/`;

    return brand === 'lincoln' && props.origin != 'videos' ? (
        <div className="breadcrumbs-style">
            <LegacyBreadcrumbs items={breadcrumbsItems} />
        </div>
    ) : (
        <div
            className={`breadcrumbAccordion onboarding-breadcrumbs--wrapper ${breadcrumbToggle &&
                'expanded'}`}
            data-testid={`onboarding-breadcrumbs-component ${props.origin}--breadcrumbs`}
        >
            <Breadcrumbs
                currentPage={currentPageBreadcrumbTitle || ''}
                type="common"
                crumbSeparator={brandBreadcrumbDelimiter}
            >
                <>
                    {newBreadcrumbsItems.map((item, index) => (
                        <>
                            <a
                                href={item.href}
                                className="breadcrumb-link"
                                aria-label={item.name}
                            >
                                {item.name}
                            </a>
                            {index < newBreadcrumbsItems.length - 1 && (
                                <span className="back-slash">
                                    {brandBreadcrumbDelimiter}
                                </span>
                            )}
                        </>
                    ))}

                    <span className="back-slash">
                        {brandBreadcrumbDelimiter}
                    </span>
                </>
            </Breadcrumbs>

            <div
                className="breadcrumb-accordion-chevron"
                onClick={() => setBreadCrumbToggle(!breadcrumbToggle)}
                data-testid="htv-breadcrumb-chevron"
            >
                <FdsChevron
                    direction={breadcrumbToggle ? 'up' : 'down'}
                    type={'unfilled'}
                />
            </div>
        </div>
    );
};

const generateBreadcrumbsItems = (
    props: Props,
    baseRoot: string,
    queryModel: string,
    queryYear: string,
    queryVin: string,
    queryCategory: string,
    brand: string
) => {
    const items = [
        {
            name: props.dyfFeatureGuideBreadcrumbs.supportBreadcrumb,
            href: `${baseRoot}`,
        },
        {
            name: props.dyfFeatureGuideBreadcrumbs.modelBreadcrumb?.replace(
                '%vehicle%',
                props.vinDetails?.vehicleName ||
                    props.ymmDetails?.modelName ||
                    formatModelName(props, getModelName(props, queryModel))
            ),
            href: props.vinDetails
                ? `${baseRoot}/vehicle/vin/${props.vinDetails.vin}`
                : `${baseRoot}/vehicle/${queryModel}/${queryYear}`,
        },
    ];

    if (props.origin === 'landing') {
        items.push({
            name: props.dyfFeatureGuideBreadcrumbs.brandBreadcrumb,
            href: '',
        });
    }

    if (props.origin === 'overview') {
        items.push(
            {
                name: props.dyfFeatureGuideBreadcrumbs?.brandBreadcrumb,
                href: getLandingHref(
                    props,
                    baseRoot,
                    queryModel,
                    queryYear,
                    brand
                ),
            },
            {
                name: (
                    props.dyfFeatureGuideBreadcrumbs
                        ?.overviewArticleBreadcrumb || ''
                ).replace(
                    '%category%',
                    StringUtil.removeHyphen(queryCategory) || ''
                ),
                href: '',
            }
        );
    }
    if (props.origin === 'related') {
        items.push(
            {
                name: props.dyfFeatureGuideBreadcrumbs?.brandBreadcrumb,
                href: `${baseRoot}/vehicle/vin/${queryVin}/discover-your-${brand.toLowerCase()}`,
            },
            {
                name: (
                    props.dyfFeatureGuideBreadcrumbs
                        ?.relatedArticlesBreadcrumb || ''
                ).replace(
                    '%category%',
                    StringUtil.removeHyphen(queryCategory) || ''
                ),
                href: '',
            }
        );
    }
    if (props.origin === 'videos') {
        items.pop();

        items.push(
            {
                name:
                    props.dyfFeatureGuideBreadcrumbs
                        ?.htvVideoLibraryBreadcrumb || '',
                href: `${baseRoot}/how-to-videos`,
            },
            {
                name:
                    queryYear +
                    ' ' +
                    formatModelName(props, getModelName(props, queryModel)),
                href: '',
            }
        );
    }
    return items;
};

const getModelName = (props: Props, queryModel: string): string => {
    if (!props.vinDetails && queryModel !== '') {
        return queryModel;
    }
    return props.vinDetails!.vehicleName;
};

const getLandingHref = (
    props: Props,
    baseRoot: string,
    queryModel: string,
    queryYear: string,
    brand: string
) => {
    if (!props.vinDetails) {
        return `${baseRoot}/vehicle/${queryModel}/${queryYear}/discover-your-${brand.toLowerCase()}`;
    }
    return `${baseRoot}/vehicle/vin/${
        props.vinDetails.vin
    }/discover-your-${brand.toLowerCase()}`;
};

const formatModelName = (props: Props, modelName: string): string => {
    if (props.vinDetails) {
        return StringUtil.capitalizeModel(modelName);
    }
    if (props.ymmDetails) {
        return props.ymmDetails.modelName;
    }
    const nameWithoutHyphen = StringUtil.removeHyphen(modelName);
    return StringUtil.capitalizeModel(nameWithoutHyphen);
};
